import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = 'ikas_access_token';

// Attach token from sessionStorage on every request (fallback if cookies blocked cross-site)
api.interceptors.request.use((config) => {
    const t = typeof window !== 'undefined' ? window.sessionStorage.getItem(TOKEN_KEY) : null;
    if (t && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

// On 401, clear token & redirect to /login — except for endpoints where 401 is a legitimate
// business error (wrong password, wrong current password, etc.) so the caller can toast the error.
const NON_LOGOUT_401_PATHS = ['/auth/me', '/auth/login', '/auth/change-password'];
api.interceptors.response.use(
    (r) => r,
    (err) => {
        const url = err.config?.url || '';
        const isBusiness401 = NON_LOGOUT_401_PATHS.some((p) => url.endsWith(p));
        if (err.response?.status === 401 && !isBusiness401) {
            if (typeof window !== 'undefined') window.sessionStorage.removeItem(TOKEN_KEY);
            delete api.defaults.headers.common['Authorization'];
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(err);
    }
);

export const fetchLeads = (params = {}) => api.get('/leads', { params }).then(r => r.data);
export const fetchLead = (id) => api.get(`/leads/${id}`).then(r => r.data);
export const createLead = (data) => api.post('/leads', data).then(r => r.data);
export const updateLead = (id, data) => api.patch(`/leads/${id}`, data).then(r => r.data);
export const deleteLead = (id) => api.delete(`/leads/${id}`).then(r => r.data);
export const scoreLead = (id) => api.post(`/leads/${id}/ai-score`).then(r => r.data);
export const scoreAllOpen = (limit = 20) => api.post(`/leads/ai-score-all-open?limit=${limit}`).then(r => r.data);

export const fetchStats = (params) => api.get('/dashboard/stats', { params }).then(r => r.data);
export const fetchFunnel = (params) => api.get('/dashboard/pipeline-funnel', { params }).then(r => r.data);
export const fetchWinLossService = (params) => api.get('/dashboard/winloss-service', { params }).then(r => r.data);
export const fetchOwnerPerformance = (params) => api.get('/dashboard/owner-performance', { params }).then(r => r.data);
export const fetchSourcePerformance = (params) => api.get('/dashboard/source-performance', { params }).then(r => r.data);
export const fetchAging = (params) => api.get('/dashboard/aging', { params }).then(r => r.data);
export const fetchArea = (params) => api.get('/dashboard/area', { params }).then(r => r.data);
export const fetchMonthPipeline = (month) => api.get('/dashboard/month-pipeline', { params: { month } }).then(r => r.data);
export const fetchMonthlyProjection = () => api.get('/analytics/monthly-projection').then(r => r.data);
export const fetchRevenue6Month = () => api.get('/analytics/revenue-6month').then(r => r.data);
export const fetchParameters = () => api.get('/parameters').then(r => r.data);
export const fetchRevenueForecast = (params) => api.get('/dashboard/revenue-forecast', { params }).then(r => r.data);

// Export
export const exportLeadsCsvUrl = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return `${API}/leads/export/csv${qs ? `?${qs}` : ''}`;
};

// Activities
export const fetchActivities = (leadId) => api.get(`/leads/${leadId}/activities`).then(r => r.data);
export const createActivity = (leadId, data) => api.post(`/leads/${leadId}/activities`, data).then(r => r.data);
export const updateActivity = (id, data) => api.patch(`/activities/${id}`, data).then(r => r.data);
export const deleteActivity = (id) => api.delete(`/activities/${id}`).then(r => r.data);
export const fetchUpcomingActivities = (days = 7) => api.get(`/activities/upcoming?days=${days}`).then(r => r.data);

export const importLeads = (file, replace = false) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/leads/import?replace=${replace}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const exportLeadsUrl = () => `${API}/leads/export/xlsx`;

// Negative leads
export const fetchNegativeLeads = (params) => api.get('/negative-leads', { params }).then(r => r.data);
export const fetchNegativeLeadsStats = () => api.get('/negative-leads/stats').then(r => r.data);
export const fetchNegativeLead = (id) => api.get(`/negative-leads/${id}`).then(r => r.data);
export const createNegativeLead = (data) => api.post('/negative-leads', data).then(r => r.data);
export const updateNegativeLead = (id, data) => api.patch(`/negative-leads/${id}`, data).then(r => r.data);
export const deleteNegativeLead = (id) => api.delete(`/negative-leads/${id}`).then(r => r.data);
export const transferNegativeToPipeline = (id) => api.post(`/negative-leads/${id}/transfer`).then(r => r.data);
export const sendLeadToNegative = (leadId, reason) => api.post(`/leads/${leadId}/send-to-negative?reason=${encodeURIComponent(reason)}`).then(r => r.data);

// Admin - users
export const fetchAdminUsers = () => api.get('/admin/users').then(r => r.data);
export const createAdminUser = (data) => api.post('/admin/users', data).then(r => r.data);
export const updateAdminUser = (id, data) => api.patch(`/admin/users/${id}`, data).then(r => r.data);
export const deleteAdminUser = (id) => api.delete(`/admin/users/${id}`).then(r => r.data);
export const changePassword = (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }).then(r => r.data);
